import { OPEN_TASK_STATUSES, type TaskSummary } from '@fos/shared/contracts/work';
import { SquareCheckBig } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router';
import { cn } from '../../../lib/cn';
import { trpc } from '../../../lib/trpc';
import { useAccess } from '../../../lib/useAccess';
import { Button } from '../../../ui/Button';
import { EmptyState, ErrorState, ListSkeleton } from '../../../ui/States';
import { TOUCH_BUTTON } from '../../mobile';
import { useAcceptRequest, useTaskMutations, useToday } from '../hooks/useTasks';
import { dueInfo } from '../lib/due';
import { NewTaskDialog } from './NewTaskDialog';
import {
  AcceptRequestButton,
  AiStatus,
  AssigneeAvatar,
  DoneToggle,
  DueText,
  FromClientBadge,
  PriorityFlag,
  RequestMark,
  TaskChips,
  TaskStatusLabel,
} from './parts';

/**
 * Otwarte zadania do wstawienia w profil klienta albo projektu (DECISIONS: „otwarte zadania” w profilu klienta).
 * Te same dane co lista i tablica; „Dodaj zadanie” otwiera dialog z uzupełnionym klientem / projektem.
 *
 *   <TaskRows filter={{ clientId: client.id }} />
 *   <TaskRows filter={{ projectId: project.id }} limit={10} />
 */
export interface TaskRowsFilter {
  clientId?: string;
  projectId?: string;
  serviceId?: string;
  assigneeUserId?: string;
}

export function TaskRows({
  filter,
  limit = 20,
  includeDone = false,
  allowAdd = true,
  emptyText = 'Brak otwartych zadań.',
}: {
  filter: TaskRowsFilter;
  limit?: number;
  /** też zadania gotowe (domyślnie tylko otwarte) */
  includeDone?: boolean;
  /** „Dodaj zadanie” w pustym stanie i pod listą (dla osób z edycją Zadań) */
  allowAdd?: boolean;
  emptyText?: string;
}) {
  const access = useAccess();
  const today = useToday();
  const { toggleDone } = useTaskMutations();
  const acceptRequest = useAcceptRequest();
  const [adding, setAdding] = useState(false);
  const list = trpc.tasks.list.useQuery(
    {
      scope: 'team',
      ...filter,
      ...(includeDone ? {} : { status: [...OPEN_TASK_STATUSES] }),
      sort: 'due',
      limit,
    },
    { enabled: access.can('tasks') },
  );

  if (!access.can('tasks')) return null;
  const canAdd = allowAdd && access.can('tasks', 'edit');
  const addButton = canAdd ? (
    <Button variant="ghost" size="s" className={TOUCH_BUTTON} onClick={() => setAdding(true)}>
      Dodaj zadanie
    </Button>
  ) : undefined;

  const allLink = new URLSearchParams({ widok: 'lista' });
  if (filter.clientId) allLink.set('klient', filter.clientId);
  if (filter.projectId) allLink.set('projekt', filter.projectId);
  if (filter.serviceId) allLink.set('usluga', filter.serviceId);

  return (
    <div>
      {list.isPending ? (
        <ListSkeleton rows={3} />
      ) : list.isError ? (
        <ErrorState compact onRetry={() => void list.refetch()} retrying={list.isFetching} />
      ) : list.data.items.length === 0 ? (
        <EmptyState compact icon={SquareCheckBig} title={emptyText} action={addButton} />
      ) : (
        <>
          <ul aria-label="Zadania">
            {list.data.items.map((task) => (
              <InlineRow
                key={task.id}
                task={task}
                today={today}
                onToggle={() => toggleDone(task)}
                onAccept={() => acceptRequest(task)}
                showClient={!filter.clientId}
                showProject={!filter.projectId}
              />
            ))}
          </ul>
          <div className="flex items-center justify-between gap-3 pt-2">
            {addButton ?? <span />}
            {list.data.truncated && (
              <Link
                to={`/zadania?${allLink.toString()}`}
                className="text-body-s font-medium text-accent hover:underline"
              >
                Wszystkie zadania
              </Link>
            )}
          </div>
        </>
      )}
      {canAdd && (
        <NewTaskDialog
          open={adding}
          onOpenChange={setAdding}
          defaultClientId={filter.clientId}
          defaultProjectId={filter.projectId}
          defaultServiceId={filter.serviceId}
        />
      )}
    </div>
  );
}

/**
 * Wiersz w profilu klienta / projektu (Figma 05 desktop i mobile): kółko „Gotowe”, tytuł, po prawej chipy
 * (desktop), stan AI albo status (gdy inny niż „Do zrobienia”, desktop), termin w kolorze znaczenia i osoba.
 * Klienta nie powtarzamy w jego własnym profilu, projektu — w profilu projektu (lifting 17.10.2026). Zgłoszenie od
 * klienta (status „Zgłoszenie”): „Przyjmij” zamiast kółka „Gotowe” (przegląd portalu 17.10.2026).
 */
function InlineRow({
  task,
  today,
  onToggle,
  onAccept,
  showClient,
  showProject,
}: {
  task: TaskSummary;
  today: string;
  onToggle: () => void;
  onAccept: () => void;
  showClient: boolean;
  showProject: boolean;
}) {
  const due = dueInfo(task.dueDate, today, { done: task.status === 'done' });
  const request = task.status === 'request';
  return (
    <li className="relative flex min-h-12 items-center gap-3.5 border-b border-line py-2.5 transition-colors hover:bg-surface">
      <span className="relative z-10 flex">
        {request ? (
          <RequestMark size={18} />
        ) : (
          <DoneToggle
            done={task.status === 'done'}
            onToggle={onToggle}
            disabled={!task.canEdit}
            title={task.title}
            size={18}
          />
        )}
      </span>
      <span className="flex min-w-0 flex-1 items-center gap-1.5">
        <Link
          to={`/zadania/${task.id}`}
          className={cn(
            'min-w-0 truncate text-body-m text-fg outline-none after:absolute after:inset-0 focus-visible:after:outline-2 focus-visible:after:-outline-offset-2 focus-visible:after:outline-accent',
            task.status === 'done' && 'text-fg-muted line-through',
          )}
        >
          {task.title}
        </Link>
        <PriorityFlag priority={task.priority} />
      </span>
      <span className="flex shrink-0 items-center gap-2.5 text-caption text-fg-muted">
        {/* przy statusie „Zgłoszenie” pigułka statusu już mówi, skąd zadanie (krótszy wiersz) */}
        {task.status !== 'request' && <FromClientBadge task={task} className="hidden md:inline-flex" />}
        <TaskChips task={task} client={showClient} project={showProject} className="hidden max-w-72 md:flex" />
        {task.aiState !== 'none' ? (
          <AiStatus state={task.aiState} className="hidden md:inline-flex" />
        ) : task.status !== 'todo' ? (
          <TaskStatusLabel status={task.status} className="hidden md:inline-flex" />
        ) : null}
        <DueText due={due} />
        {request && task.canEdit ? (
          <AcceptRequestButton task={task} onAccept={onAccept} />
        ) : (
          <AssigneeAvatar assignee={task.assignee} />
        )}
      </span>
    </li>
  );
}
